import { RefObject, useCallback, useRef, useState } from 'react';

import Button from 'components/buttons/Button';

import { useBodyScrollLock } from 'hooks/useBodyScrollLock';
import { useClickOutside } from 'hooks/useClickOutside';

import ChevronIcon from 'assets/events/icons/chevron-icon.svg';
import CrossIcon from 'assets/events/icons/cross-icon.svg';

import { rewardTypeOptions, sortByOptions, specialOptions, statusOptions } from '../constants';
import { useEventsFilters } from '../context/EventsFiltersContext';
import { FilterCheckbox } from './FilterCheckbox';
import { FilterHeading } from './FilterHeading';

type FiltersSidebarProps = {
  onClose: () => void;
  excludeRefs?: RefObject<HTMLElement | null>[];
  isOpen: boolean;
};

export const FiltersSidebar = ({ onClose, excludeRefs, isOpen }: FiltersSidebarProps) => {
  const sidebarRef = useRef<HTMLDivElement>(null);
  const { filters, setFilters, resetFilters, areFiltersDefault } = useEventsFilters();
  const [expandedSections, setExpandedSections] = useState({
    sortBy: true,
    status: true,
    special: false,
    rewardType: false,
  });

  useClickOutside(sidebarRef, onClose, excludeRefs);
  useBodyScrollLock(isOpen);

  const toggleSection = useCallback((section: keyof typeof expandedSections) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  }, []);

  const handleSortByChange = useCallback(
    (option: string) => {
      setFilters((prev) => ({ ...prev, sortBy: option }));
    },
    [setFilters]
  );

  const handleMultiSelectChange = useCallback(
    (filterKey: 'status' | 'rewardType') => (option: string, checked: boolean) => {
      setFilters((prev) => {
        const currentFilter = { ...prev[filterKey] };

        if (option === 'ALL') {
          if (checked) {
            Object.keys(currentFilter).forEach((key) => {
              currentFilter[key] = key === 'ALL';
            });
          }
        } else {
          currentFilter[option] = checked;
          if (checked) {
            currentFilter.ALL = false;
          } else {
            const hasAnySelected = Object.entries(currentFilter).some(([key, selected]) => key !== 'ALL' && selected);
            if (!hasAnySelected) {
              currentFilter.ALL = true;
            }
          }
        }

        return { ...prev, [filterKey]: currentFilter };
      });
    },
    [setFilters]
  );

  const handleStatusChange = useCallback(
    (option: string, checked: boolean) => handleMultiSelectChange('status')(option, checked),
    [handleMultiSelectChange]
  );
  const handleRewardTypeChange = useCallback(
    (option: string, checked: boolean) => handleMultiSelectChange('rewardType')(option, checked),
    [handleMultiSelectChange]
  );

  const handleSpecialChange = useCallback(
    (option: string, checked: boolean) => {
      setFilters((prev) => ({
        ...prev,
        special: { ...prev.special, [option]: checked },
      }));
    },
    [setFilters]
  );

  const handleClearFilters = useCallback(() => {
    resetFilters();
  }, [resetFilters]);

  const toggleSortBy = useCallback(() => toggleSection('sortBy'), [toggleSection]);
  const toggleStatus = useCallback(() => toggleSection('status'), [toggleSection]);
  const toggleSpecial = useCallback(() => toggleSection('special'), [toggleSection]);
  const toggleRewardType = useCallback(() => toggleSection('rewardType'), [toggleSection]);

  const createSortByHandler = useCallback(
    (optionId: string) => () => handleSortByChange(optionId),
    [handleSortByChange]
  );

  const createStatusHandler = useCallback(
    (optionId: string) => (checked: boolean) => handleStatusChange(optionId, checked),
    [handleStatusChange]
  );

  const createSpecialHandler = useCallback(
    (optionId: string) => (checked: boolean) => handleSpecialChange(optionId, checked),
    [handleSpecialChange]
  );

  const createRewardTypeHandler = useCallback(
    (optionId: string) => (checked: boolean) => handleRewardTypeChange(optionId, checked),
    [handleRewardTypeChange]
  );

  return (
    <>
      {isOpen && (
        <div
          className="tw-fixed tw-left-0 tw-top-[62px] tw-z-[97] tw-h-[calc(var(--vh,1vh)*100-62px)] tw-w-full tw-bg-[rgba(16,16,16,0.7)] tw-transition-opacity tw-duration-200 tw-ease-in-out bp-3xl:tw-top-[70px] bp-3xl:tw-h-[calc(var(--vh,1vh)*100-70px)] bp-4xl:tw-top-[86px] bp-4xl:tw-h-[calc(var(--vh,1vh)*100-86px)]"
          onClick={onClose}
          aria-hidden="true"
        />
      )}
      <div
        ref={sidebarRef}
        style={{
          transform: isOpen ? 'translateX(0)' : 'translateX(-100%)',
          overscrollBehaviorY: 'contain',
          touchAction: 'pan-y',
        }}
        className={`tw-fixed tw-left-0 tw-top-[62px] tw-z-[98] tw-h-[calc(var(--vh,1vh)*100-62px)] tw-w-full tw-overflow-y-auto tw-bg-[#202020] tw-transition-transform tw-duration-200 tw-ease-in-out ${
          !isOpen ? 'tw-pointer-events-none' : ''
        } lg:tw-w-[344px] bp-3xl:tw-top-[70px] bp-3xl:tw-h-[calc(var(--vh,1vh)*100-70px)] bp-4xl:tw-top-[86px] bp-4xl:tw-h-[calc(var(--vh,1vh)*100-86px)]`}
      >
        <div className="tw-flex tw-w-full tw-flex-col tw-items-start tw-gap-[10px] tw-px-[20px] tw-pb-[20px] tw-pt-[20px] lg:tw-pb-[20px]">
          <FilterHeading icon={CrossIcon} onClick={onClose} />
          <div className="tw-flex tw-w-full tw-flex-col tw-items-start tw-gap-[20px]">
            <div className="tw-flex tw-w-full tw-flex-col tw-items-start tw-gap-[10px]">
              <FilterHeading
                title="Sort by"
                icon={ChevronIcon}
                iconRotation={expandedSections.sortBy ? 'up' : 'down'}
                onClick={toggleSortBy}
              />
              {expandedSections.sortBy &&
                sortByOptions.map((option) => (
                  <FilterCheckbox
                    key={option.id}
                    title={option.label}
                    checked={filters.sortBy === option.id}
                    onChange={createSortByHandler(option.id)}
                    isSingleSelection
                  />
                ))}
            </div>

            <div className="tw-flex tw-w-full tw-flex-col tw-items-start tw-gap-[10px]">
              <FilterHeading
                title="Status"
                icon={ChevronIcon}
                iconRotation={expandedSections.status ? 'up' : 'down'}
                onClick={toggleStatus}
              />
              {expandedSections.status &&
                statusOptions.map((option) => (
                  <FilterCheckbox
                    key={option.id}
                    title={option.label}
                    checked={filters.status[option.id] || false}
                    onChange={createStatusHandler(option.id)}
                  />
                ))}
            </div>

            <div className="tw-flex tw-w-full tw-flex-col tw-items-start tw-gap-[10px]">
              <FilterHeading
                title="Special"
                icon={ChevronIcon}
                iconRotation={expandedSections.special ? 'up' : 'down'}
                onClick={toggleSpecial}
              />
              {expandedSections.special &&
                specialOptions.map((option) => (
                  <FilterCheckbox
                    key={option.id}
                    title={option.label}
                    checked={filters.special[option.id]}
                    onChange={createSpecialHandler(option.id)}
                  />
                ))}
            </div>

            <div className="tw-flex tw-w-full tw-flex-col tw-items-start tw-gap-[10px]">
              <FilterHeading
                title="Reward type"
                icon={ChevronIcon}
                iconRotation={expandedSections.rewardType ? 'up' : 'down'}
                onClick={toggleRewardType}
              />
              {expandedSections.rewardType &&
                rewardTypeOptions.map((option) => (
                  <FilterCheckbox
                    key={option.id}
                    title={option.label}
                    checked={filters.rewardType[option.id]}
                    onChange={createRewardTypeHandler(option.id)}
                  />
                ))}
            </div>

            <Button
              text="Restore Defaults"
              size="events"
              color="referral-cta"
              onClick={handleClearFilters}
              className="tw-w-full tw-capitalize"
              disabled={areFiltersDefault}
            />
          </div>
        </div>
      </div>
    </>
  );
};
